import { memo, useContext } from 'react';
import { formatCurrency } from '@atc/string-fns';
import { useElementObserver } from '@atc/use-element-observer';
import { useFeatures } from '@atc/features';
import { ratesLabels } from 'axl-config';
import clsx from 'clsx';
import ndsConstants from '../ndsConstants.js';
import CalculatorInput from './CalculatorInput.js';
import TradeInfoInputs from './TradeInfoInputs.js';
import ConfigContext from '../../ConfigContext/ConfigContext.js';
import TermSelect from './TermSelect.js';
import SelectButtonContainer from './SelectButtonContainer.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
const {
  loanTermOptions,
  loanTermOptionsExcellentTier
} = ndsConstants;

/**
 * Finance tab of Native Deal Calculator
 *
 * Renders the finance tab content with calculator inputs for vehicle financing including
 * credit rating, down payment, loan terms, and optional trade-in information.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<{label: string, value: string}>} props.creditTierOptions - Array of credit tier options for the dropdown select
 * @param {React.Node} props.creditVerificationLink - Credit verification link element
 * @param {number} props.customDefaultFinanceTerm - Custom default finance term value
 * @param {Function} props.onSupportingLinkClick - Handler for supporting link clicks
 * @param {boolean} props.isSupportingLinkClicked - Whether supporting link has been clicked
 * @param {number} props.financeDownPayment - Down payment amount for financing
 * @param {string} props.formattedTotalPrice - Formatted total price of the vehicle
 * @param {Function} props.handleSelectChange - Handler for select input changes
 * @param {Object} props.inputProps - Props to pass to input elements
 * @param {Array} props.lendingOptions - Available lending options
 * @param {string} props.previousCompletedInput - Name of the most recently completed input field
 * @param {Function} props.renderTaxesAndFees - Function to render taxes and fees section for a given tab
 * @param {string} props.selectedCreditTier - Currently selected credit tier value
 * @param {string|number} props.selectedLoanTerm - Currently selected loan term value
 * @param {boolean} props.showOfferPrice - Whether to show the offer price
 * @param {Object} props.tradeInfoInputProps - Props for the trade-in input fields
 * @returns {JSX.Element} The FinanceTab component
 */
function FinanceTab(_ref) {
  let {
    creditTierOptions,
    creditVerificationLink,
    customDefaultFinanceTerm,
    onSupportingLinkClick,
    isSupportingLinkClicked,
    financeDownPayment,
    formattedTotalPrice,
    handleSelectChange,
    inputProps,
    isLoading,
    lendingOptions,
    previousCompletedInput,
    renderTaxesAndFees,
    selectedCreditTier,
    selectedLoanTerm,
    showOfferPrice,
    tradeInfoInputProps
  } = _ref;
  const {
    disableCreditTierSelection = false,
    disableTrade,
    enableCreditVerification,
    enableNegotiation,
    oemLabels = {},
    showRatesTable,
    selectedCriteria: {
      creditLowScore,
      financeTermOptions = []
    } = {}
  } = useContext(ConfigContext) ?? {};
  const {
    cashDownLabelDS
  } = oemLabels;
  const {
    credit_verification: [creditVerificationEnabled]
  } = useFeatures(['credit_verification']);
  const showCreditVerificationCTA = enableCreditVerification || creditVerificationEnabled;
  const [containerElement, containerRef] = useElementObserver();
  const negotiableOfferLabel = showOfferPrice ? 'Offer Price' : 'Listing Price';

  /*
   * Populate finance terms
   */
  let addedLoanTerms;
  if (financeTermOptions.length > 0) {
    // Use dynamic terms provided by selectedCriteria
    addedLoanTerms = financeTermOptions;
  } else {
    // Fall back to default hardcoded terms
    addedLoanTerms = selectedCreditTier === 'excellent' ? loanTermOptionsExcellentTier : loanTermOptions;
  }
  if (customDefaultFinanceTerm !== null && customDefaultFinanceTerm > 0 && !addedLoanTerms.includes(customDefaultFinanceTerm)) {
    addedLoanTerms = [...addedLoanTerms, customDefaultFinanceTerm].sort((a, b) => a - b);
  }
  if (creditLowScore && creditTierOptions?.length) {
    // replace the three-digit low credit score only
    creditTierOptions[4].label = creditTierOptions[4].label.replace(/\((\d+)-(\d+)\)/, (_, __, highScore) => `(${creditLowScore}-${highScore})`);
  }
  const creditRatingChildren = /*#__PURE__*/jsx(TermSelect, {
    name: "creditTierInput",
    id: "creditTierInput-finance",
    label: "Credit Rating",
    labelClassName: "display-none display-sm-block text-size-300",
    value: selectedCreditTier,
    options: creditTierOptions,
    handleChange: handleSelectChange,
    supportingLink: showCreditVerificationCTA && creditVerificationLink
  });
  const downPaymentChildren = /*#__PURE__*/jsx(CalculatorInput, {
    name: "downPaymentInput",
    label: cashDownLabelDS ?? 'Down Payment',
    value: financeDownPayment,
    supportingLink: false,
    tabName: "finance",
    inputProps: {
      ...inputProps,
      className: clsx({
        'margin-bottom-4': tradeInfoInputProps?.digitalRetailingType === 'psx'
      })
    }
  }, "calculatorInput");
  const listingPriceChildren = /*#__PURE__*/jsx(CalculatorInput, {
    name: "offerPriceInput",
    label: negotiableOfferLabel,
    inputProps: {
      ...inputProps,
      className: clsx('margin-bottom-4', {
        'margin-top-4': enableNegotiation
      })
    },
    value: formattedTotalPrice,
    supportingLink: false,
    tabName: "finance",
    showTextField: !enableNegotiation
  }, "offerPriceInput");
  const tradeInChildren = !disableTrade && /*#__PURE__*/jsx(TradeInfoInputs, {
    ...tradeInfoInputProps,
    inputProps: inputProps,
    tabName: "finance"
  });
  const termsChildren = /*#__PURE__*/jsx(TermSelect, {
    value: selectedLoanTerm,
    options: addedLoanTerms,
    name: "termInput",
    label: "Finance Terms",
    labelClassName: "display-none display-sm-block text-size-300",
    handleChange: handleSelectChange,
    rates: showRatesTable ? lendingOptions : undefined,
    isLoading: isLoading
  });

  // Add inputs props for the new design here.
  const calcInputs = [{
    heading: 'Finance Terms',
    primaryLabel: 'Finance Terms',
    children: termsChildren,
    secondaryLabel: `${selectedLoanTerm} months`,
    containerElement,
    inputCompletedState: previousCompletedInput === 'termInput',
    analyticProps: {
      cmp: 'nds_finance-term',
      userModified: 'FINANCE_TERM_LENGTH_CHANGED'
    },
    'data-cmp': 'terms'
  }, {
    heading: 'Credit Rating',
    primaryLabel: 'Credit Rating',
    children: creditRatingChildren,
    disabled: disableCreditTierSelection,
    secondaryLabel: ratesLabels[selectedCreditTier]?.label,
    position: 'right',
    containerElement,
    inputCompletedState: previousCompletedInput === 'creditTierInput',
    analyticProps: {
      cmp: 'nds_credit-rating',
      userModified: 'CREDIT_VERIFICATION_CLICKED'
    },
    onSupportingLinkClick,
    isSupportingLinkClicked,
    'data-cmp': 'creditRating'
  }];
  const tradeInCalcInput = {
    analyticProps: {
      cmp: 'nds_trade-in',
      userModified: 'TRADE_IN_VALUE_CHANGED'
    },
    children: tradeInChildren,
    containerElement,
    'data-cmp': 'tradeIn',
    heading: 'Trade-In',
    inputCompletedState: previousCompletedInput === 'tradeInInput',
    position: 'right',
    primaryLabel: 'Trade-In',
    secondaryLabel: formatCurrency(tradeInfoInputProps.valueTradeIn - tradeInfoInputProps.valueOwed || 0)
  };

  // Add trade-in to the front of the array if trade is not disabled
  if (!disableTrade) {
    calcInputs.unshift(tradeInCalcInput);
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [listingPriceChildren, /*#__PURE__*/jsxs("div", {
      className: "row",
      ref: containerRef,
      children: [/*#__PURE__*/jsx("div", {
        className: disableTrade ? 'col-xs-12' : 'col-xs-6',
        children: downPaymentChildren
      }), /*#__PURE__*/jsx(SelectButtonContainer, {
        calcInputs: calcInputs
      })]
    }), renderTaxesAndFees('finance')]
  });
}
var FinanceTab$1 = /*#__PURE__*/memo(FinanceTab);
export { FinanceTab$1 as default };
